import { ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators'

import { combineEpicsIsolated } from 'utils/epicRegistry'

import { addToast } from 'containers/Toasts/actions'

import {
  getGwsSuperAdminEmail,
  setGwsSuperAdminEmail,
} from 'components/pages/partner/OnboardingWizard/sessionStorage'

import {
  wizardCreateGwsAdminError,
  wizardCreateGwsAdminSuccess,
  wizardCreateTenantError,
  wizardCreateTenantSuccess,
  wizardEnableDmarc,
  wizardEnableDmarcError,
  wizardEnableDmarcSuccess,
  wizardEnableGwsError,
  wizardEnableGwsSuccess,
  wizardFetchFailedMailboxesError,
  wizardFetchFailedMailboxesSuccess,
  wizardFetchGroupsError,
  wizardFetchGroupsSuccess,
  wizardFetchStatus,
  wizardFetchStatusError,
  wizardFetchStatusSuccess,
  wizardGetSettingsOptionsError,
  wizardGetSettingsOptionsSuccess,
  wizardSaveSettingsError,
  wizardSaveSettingsSuccess,
  wizardSetStep,
  wizardTriggerSyncError,
  wizardTriggerSyncSuccess,
} from './actions'
import {
  WIZARD_CREATE_GWS_ADMIN,
  WIZARD_CREATE_TENANT,
  WIZARD_ENABLE_DMARC,
  WIZARD_ENABLE_GWS,
  WIZARD_FETCH_FAILED_MAILBOXES,
  WIZARD_FETCH_GROUPS,
  WIZARD_FETCH_STATUS,
  WIZARD_GET_SETTINGS_OPTIONS,
  WIZARD_SAVE_SETTINGS,
  WIZARD_TRIGGER_SYNC,
} from './constants'

const createTenantEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(WIZARD_CREATE_TENANT.INITIATE),
    switchMap((action) => {
      const { dmarcEnabled, dmarcPlan } = action.payload
      return api.onboardingWizard.createTenant(action.payload).pipe(
        mergeMap((response) => {
          const actions = [wizardCreateTenantSuccess(response)]
          if (dmarcEnabled) {
            actions.push(wizardEnableDmarc(response.company_id, { dmarc_plan: dmarcPlan }))
          }
          return of(...actions)
        }),
        catchError((error) => of(epicsErrorHandler(wizardCreateTenantError, error, [400])))
      )
    })
  )

const fetchStatusEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(WIZARD_FETCH_STATUS.INITIATE),
    switchMap((action) => {
      const { companyId } = action.payload
      return api.onboardingWizard.fetchStatus(companyId).pipe(
        map((response) => wizardFetchStatusSuccess(response)),
        catchError((error) => of(epicsErrorHandler(wizardFetchStatusError, error, [400])))
      )
    })
  )

const triggerSyncEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(WIZARD_TRIGGER_SYNC.INITIATE),
    switchMap((action) => {
      const { companyId, ...data } = action.payload
      return api.onboardingWizard.triggerSync(companyId, data).pipe(
        mergeMap((response) =>
          of(wizardTriggerSyncSuccess(response), wizardFetchStatus(companyId))
        ),
        catchError((error) => of(epicsErrorHandler(wizardTriggerSyncError, error, [400])))
      )
    })
  )

const getSettingsOptionsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(WIZARD_GET_SETTINGS_OPTIONS.INITIATE),
    switchMap((action) => {
      const { companyId } = action.payload
      return api.onboardingWizard.getSettingsOptions(companyId).pipe(
        map((response) => wizardGetSettingsOptionsSuccess(response)),
        catchError((error) => of(epicsErrorHandler(wizardGetSettingsOptionsError, error, [400])))
      )
    })
  )

const saveSettingsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(WIZARD_SAVE_SETTINGS.INITIATE),
    switchMap((action) => {
      const { companyId, ...data } = action.payload
      return api.onboardingWizard.saveSettings(companyId, data).pipe(
        mergeMap((response) =>
          of(
            wizardSaveSettingsSuccess(response),
            wizardSetStep(6),
            addToast({ message: 'Settings saved.' })
          )
        ),
        catchError((error) => of(epicsErrorHandler(wizardSaveSettingsError, error, [400])))
      )
    })
  )

const enableDmarcEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(WIZARD_ENABLE_DMARC.INITIATE),
    switchMap((action) => {
      const { companyId, ...data } = action.payload
      return api.onboardingWizard.enableDmarc(companyId, data).pipe(
        mergeMap((response) =>
          of(wizardEnableDmarcSuccess(response), addToast({ message: 'DMARC management enabled.' }))
        ),
        catchError((error) => of(epicsErrorHandler(wizardEnableDmarcError, error, [400])))
      )
    })
  )

const fetchGroupsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(WIZARD_FETCH_GROUPS.INITIATE),
    switchMap((action) => {
      const { companyId, refresh } = action.payload
      return api.onboardingWizard.fetchGroups(companyId, refresh).pipe(
        map((response) => wizardFetchGroupsSuccess(response)),
        catchError((error) => of(epicsErrorHandler(wizardFetchGroupsError, error, [400])))
      )
    })
  )

const fetchFailedMailboxesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(WIZARD_FETCH_FAILED_MAILBOXES.INITIATE),
    switchMap((action) => {
      const { companyId } = action.payload
      return api.onboardingWizard.fetchFailedMailboxes(companyId).pipe(
        map((response) => wizardFetchFailedMailboxesSuccess(response)),
        catchError((error) => of(epicsErrorHandler(wizardFetchFailedMailboxesError, error, [400])))
      )
    })
  )

const createGwsAdminEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(WIZARD_CREATE_GWS_ADMIN.INITIATE),
    switchMap((action) => {
      const { companyId, ...data } = action.payload
      return api.onboardingWizard.createGwsSuperAdmin(companyId, data).pipe(
        mergeMap((response) => {
          setGwsSuperAdminEmail(companyId, response.email)
          return of(
            wizardCreateGwsAdminSuccess(response),
            addToast({ message: 'Super Admin mailbox added.' })
          )
        }),
        catchError((error) => of(epicsErrorHandler(wizardCreateGwsAdminError, error, [400])))
      )
    })
  )

const enableGwsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(WIZARD_ENABLE_GWS.INITIATE),
    switchMap((action) => {
      const { companyId } = action.payload
      const adminEmail = getGwsSuperAdminEmail(companyId)
      return api.onboardingWizard.enableGmailWebhooks(companyId, adminEmail).pipe(
        mergeMap((response) => of(wizardEnableGwsSuccess(response), wizardFetchStatus(companyId))),
        catchError((error) => of(epicsErrorHandler(wizardEnableGwsError, error, [400])))
      )
    })
  )

export default combineEpicsIsolated(
  createTenantEpic,
  fetchStatusEpic,
  triggerSyncEpic,
  getSettingsOptionsEpic,
  saveSettingsEpic,
  enableDmarcEpic,
  fetchGroupsEpic,
  fetchFailedMailboxesEpic,
  createGwsAdminEpic,
  enableGwsEpic
)
